import React, {useEffect, useState} from 'react'
import {IWizardDocument} from '../../../models/application-form'
import {UploadSlotDefinition} from './uploadSlots'

const DEFAULT_MY_GOV_LINK_URL = 'https://my.gov.au/'

interface UploadSlotRowProps {
  slot: UploadSlotDefinition
  documentName?: string
  document?: IWizardDocument
  isUploading?: boolean
  uploadProgress?: number
  accept?: string
  previewUrl?: string
  onUploadFile: (file: File) => void
  onPreview?: () => void
  onClear: () => void
  allowPreview?: boolean
  isSavedForLater?: boolean
  onSaveForLater?: () => void
  onUndoSaveForLater?: () => void
}

const UploadSlotRow: React.FC<UploadSlotRowProps> = ({
  slot,
  document,
  documentName,
  isUploading = false,
  uploadProgress = 0,
  accept = '.png,.jpg,.jpeg,application/pdf',
  previewUrl,
  onUploadFile,
  onPreview,
  onClear,
  allowPreview = true,
  isSavedForLater = false,
  onSaveForLater,
  onUndoSaveForLater,
}) => {
  const isUploaded = Boolean(document?.filename?.trim())
  const shouldShowSavedForLater = isSavedForLater && !isUploaded
  const fileInputRef = React.useRef<HTMLInputElement | null>(null)
  const isImage = Boolean(document?.contentType?.toLowerCase().startsWith('image/'))
  const normalizedProgress = Math.min(100, Math.max(0, Math.round(uploadProgress)))
  const [expanded, setExpanded] = useState(false)
  const shouldShowMyGovLink = slot.showMyGovLink && !isUploaded && !shouldShowSavedForLater

  const openFilePicker = () => {
    fileInputRef.current?.click()
  }

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    onUploadFile(file)
    event.target.value = ''
  }

  useEffect(() => {
    if (isUploaded) {
      setExpanded(false) // collapsed by default
    }
  }, [isUploaded])

  const renderSlotPill = () => {
    if (shouldShowSavedForLater) {
      return <span className='pill pill-later ms-2'>Later</span>
    }

    if (slot.required && slot.showRequired) {
      return <span className='pill pill-req ms-2'>Required</span>
    }

    if (slot.optional) {
      return <span className='pill pill-optional ms-2'>Optional</span>
    }

    return null
  }

  const renderSlotHeader = () => (
    <div className='form-label fw-semibold mt-0 mb-2 d-flex align-items-start justify-content-between gap-3 flex-wrap'>
      <span className='min-w-0'>
        {slot.label}
        {renderSlotPill()}
      </span>

      {shouldShowMyGovLink && (
        <a
          href={slot.myGovLinkUrl ?? DEFAULT_MY_GOV_LINK_URL}
          target='_blank'
          rel='noopener noreferrer'
          className='small text-primary text-decoration-underline text-nowrap ms-auto'
        >
          Get it from myGov
        </a>
      )}
    </div>
  )

  if (shouldShowSavedForLater) {
    return (
      <div className='upload-slot-row'>
        {renderSlotHeader()}
        {slot.helperText && (
          <small className='text-muted d-block mt-1 fs-6'>{slot.helperText}</small>
        )}
        {slot.stronglyRecommended && <strong className='text-grad'>Strongly recommended.</strong>}

        <div className='upload-saved-later d-flex align-items-center justify-content-between gap-3 my-3'>
          <div className='d-flex align-items-center min-w-0'>
            <span className='fw-semibold ms-3 fs-5'>
              Saved for later. Card will show this as incomplete.
            </span>
          </div>

          {onUndoSaveForLater && (
            <button
              type='button'
              className='btn btn-sm btn-light border p-0 bg-transparent border-0 fs-5'
              onClick={onUndoSaveForLater}
            >
              Undo
            </button>
          )}
        </div>
      </div>
    )
  }

  if (isUploaded && !allowPreview) {
    return (
      <div className='upload-slot-row'>
        {renderSlotHeader()}
        {slot.helperText && (
          <small className='text-muted d-block mt-1 fs-6'>{slot.helperText}</small>
        )}
        {slot.stronglyRecommended && <strong className='text-grad'>Strongly recommended.</strong>}

        <div className='upload-simple-row d-flex align-items-center justify-content-between gap-3'>
          <div className='upload-simple-row__file d-flex align-items-center'>
            <svg
              className='flex-shrink-0'
              width='16'
              height='16'
              viewBox='0 0 20 20'
              fill='none'
              stroke='#0A7C5A'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M3.5 10.5l4 4 9-9'></path>
            </svg>
            <span className='upload-file-name fw-semibold ms-3 fs-4' title={document?.filename}>
              {document?.filename}
            </span>
          </div>

          <button
            type='button'
            className='btn btn-sm btn-light border p-0 bg-transparent border-0 fs-5 flex-shrink-0'
            title='Remove upload'
            aria-label='Remove upload'
            onClick={onClear}
          >
            Remove
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className='upload-slot-row'>
      {renderSlotHeader()}
      {slot.helperText && <small className='text-muted d-block mt-1 fs-6'>{slot.helperText}</small>}

      {slot.stronglyRecommended && <strong className='text-grad'>Strongly recommended.</strong>}
      {/* SUCCESS COLLAPSIBLE HEADER */}
      {isUploaded && (
        <button
          type='button'
          className={`upload-success-banner d-flex align-items-center justify-content-center ${
            expanded ? 'expanded' : ''
          }`}
          onClick={() => setExpanded((prev) => !prev)}
        >
          <div className='d-flex align-items-center'>
            <i className='bi bi-check-lg me-2 text-success'></i>
            <span className='lh-sm'>{documentName} uploaded successfully</span>
          </div>

          <i
            className={`bi ms-3 text-success ${expanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}
          ></i>
        </button>
      )}

      {/* COLLAPSIBLE CONTENT */}
      {(!isUploaded || expanded) && (
        <div
          className={`wizard-upload-slot mt-0 position-relative py-3 ${
            isUploaded ? 'uploaded' : ''
          }`}
        >
          {isUploaded && (
            <button
              type='button'
              className='btn btn-sm btn-light border upload-clear-btn'
              title='Clear upload'
              aria-label='Clear upload'
              onClick={onClear}
            >
              <i className='bi bi-x-lg'></i>
            </button>
          )}

          {isUploading && (
            <div className='mt-2'>
              <div className='progress' style={{height: 6}}>
                <div
                  className='progress-bar'
                  role='progressbar'
                  style={{width: `${normalizedProgress}%`}}
                  aria-valuenow={normalizedProgress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                ></div>
              </div>
              <div className='small text-muted mt-1'>Uploading... {normalizedProgress}%</div>
            </div>
          )}

          <input
            ref={fileInputRef}
            type='file'
            accept={accept}
            className='d-none'
            onChange={handleFileSelected}
          />

          {/* Preview */}
          {isUploaded && (
            <>
              {previewUrl && isImage && (
                <div className='text-center mb-3'>
                  <img
                    src={previewUrl}
                    alt={document?.filename || slot.label}
                    className='upload-preview'
                  />
                </div>
              )}

              <div className='text-center mb-3'>
                <div className='upload-file-name fw-semibold' title={document?.filename}>
                  {document?.filename}
                </div>

                {document?.contentType && (
                  <div className='text-muted small'>({document.contentType})</div>
                )}
              </div>
            </>
          )}

          {/* Actions */}
          <div className='d-grid gap-2'>
            <button
              type='button'
              className='btn btn-light border w-100 d-flex align-items-center justify-content-center'
              onClick={openFilePicker}
              disabled={isUploading}
            >
              <i className='bi bi-upload me-2'></i>

              {isUploading
                ? 'Uploading...'
                : isUploaded
                ? `Re-upload ${documentName ?? ''}`
                : `Upload ${documentName ?? ''}`}
            </button>

            {isUploaded && onPreview && (
              <button
                type='button'
                className='btn btn-light border d-flex align-items-center justify-content-center px-3'
                onClick={onPreview}
              >
                <i className='bi bi-eye'></i> Preview
              </button>
            )}
          </div>

          {onSaveForLater && !isUploaded && (
            <div className='text-end mt-3'>
              <button
                type='button'
                className='btn btn-sm btn-link text-blue fs-6 text-decoration-none p-0'
                onClick={onSaveForLater}
                disabled={isUploading}
              >
                I'll upload later <i className='bi bi-arrow-right ms-1 text-blue'></i>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default UploadSlotRow
